'use client';
import { jsxs, jsx, Fragment } from 'react/jsx-runtime';
import { useState, useMemo, useCallback, useEffect } from 'react';
import usePrevious from 'react-use/lib/usePrevious';
import styled, { useTheme } from 'styled-components';
import { addHasChildren } from '../../helpers/addHasChildren.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { useGreaterThan } from '../../hooks/useGreaterThan.js';
import { useToggle } from '../../hooks/useToggle.js';
import { staticTreeSource } from '../../hooks/useTree/StaticTreeSource.js';
import { TreeContainer } from '../../hooks/useTree/TreeContainer.js';
import '../../hooks/useTree/useTreeContent.js';
import '../../hooks/useTree/useTreeController.js';
import { media } from '../../mixins/media.js';
import { spacing } from '../../mixins/spacing.js';
import { Drawer } from '../Drawer/Drawer.js';
import { DrawerError } from '../Drawer/DrawerError.js';
import '../ElementProps/ElementPropsProvider.js';
import { useElementProps } from '../ElementProps/useElementProps.js';
import { IconButton } from '../IconButton/IconButton.js';
import '../Icons/AlignStartHorizontalIcon.js';
import '../Icons/ArchiveIcon.js';
import '../Icons/ArrowDownIcon.js';
import '../Icons/ArrowLeftBottomIcon.js';
import '../Icons/ArrowLeftIcon.js';
import '../Icons/ArrowLeftTopIcon.js';
import '../Icons/ArrowRightBottomIcon.js';
import '../Icons/ArrowRightIcon.js';
import '../Icons/ArrowRightTopIcon.js';
import '../Icons/BagIcon.js';
import '../Icons/BasketIcon.js';
import '../Icons/BellFilledIcon.js';
import '../Icons/BellNotificationIcon.js';
import '../Icons/BellOutlineIcon.js';
import '../Icons/BinocularsIcon.js';
import '../Icons/BlocksIcon.js';
import '../Icons/BoltIcon.js';
import '../Icons/BookIcon.js';
import '../Icons/BookmarkAddFilledIcon.js';
import '../Icons/BookmarkAddOutlineIcon.js';
import '../Icons/BookmarkIcon.js';
import '../Icons/BookOpenedIcon.js';
import '../Icons/BoxAltIcon.js';
import '../Icons/BusinessIcon.js';
import '../Icons/CalendarIcon.js';
import '../Icons/CancelIcon.js';
import '../Icons/CardsIcon.js';
import '../Icons/ChatAltIcon.js';
import '../Icons/ChatIcon.js';
import '../Icons/CheckboxIcon.js';
import '../Icons/CheckCirlcleFilledIcon.js';
import '../Icons/CheckSquareIcon.js';
import '../Icons/ChevronDoubleLeftIcon.js';
import '../Icons/ChevronDoubleRightIcon.js';
import '../Icons/ChevronDownIcon.js';
import '../Icons/ChevronLeftIcon.js';
import '../Icons/ChevronRightIcon.js';
import '../Icons/ChevronUpIcon.js';
import '../Icons/ClipboardEditIcon.js';
import '../Icons/ClipboardIcon.js';
import { CloseIcon } from '../Icons/CloseIcon.js';
import '../Icons/CollapseIcon.js';
import '../Icons/ControlsIcon.js';
import '../Icons/ControlsVerticalAltIcon.js';
import '../Icons/CopyIcon.js';
import '../Icons/CutIcon.js';
import '../Icons/DangerIcon.js';
import '../Icons/DownloadCloudIcon.js';
import '../Icons/DownloadIcon.js';
import '../Icons/DownloadPdfIcon.js';
import '../Icons/DrawerOpenIcon.js';
import '../Icons/EllipsisIcon.js';
import '../Icons/EllypsisVerticalIcon.js';
import '../Icons/EnvelopeIcon.js';
import '../Icons/ErrorIcon.js';
import '../Icons/ExitIcon.js';
import '../Icons/ExpandIcon.js';
import '../Icons/ExternalLinkIcon.js';
import '../Icons/EyeCrossedIcon.js';
import '../Icons/EyeIcon.js';
import '../Icons/FacebookIcon.js';
import '../Icons/FavouriteIcon.js';
import '../Icons/FileBadgeIcon.js';
import '../Icons/FileIcon.js';
import '../Icons/FileSearchIcon.js';
import '../Icons/FileType2Icon.js';
import '../Icons/FilmIcon.js';
import '../Icons/FitscreenIcon.js';
import '../Icons/FocusIcon.js';
import '../Icons/FolderAddIcon.js';
import '../Icons/FolderClosedIcon.js';
import '../Icons/FolderIcon.js';
import '../Icons/FutureIcon.js';
import '../Icons/GalleryVerticalEndIcon.js';
import '../Icons/GridIcon.js';
import '../Icons/HelpIcon.js';
import '../Icons/HistoryIcon.js';
import '../Icons/HomeIcon.js';
import '../Icons/InformationIcon.js';
import '../Icons/InstagramIcon.js';
import '../Icons/LibraryIcon.js';
import '../Icons/LightbulbIcon.js';
import '../Icons/LinkedinIcon.js';
import '../Icons/ListIcon.js';
import '../Icons/ListSearchIcon.js';
import '../Icons/Loader2Icon.js';
import '../Icons/LockAltIcon.js';
import '../Icons/LockAltOpenIcon.js';
import '../Icons/LockIcon.js';
import '../Icons/LockOpenIcon.js';
import '../Icons/MagazineIcon.js';
import '../Icons/MailIcon.js';
import '../Icons/Maximize2Icon.js';
import '../Icons/MenuCollapsable2Icon.js';
import '../Icons/MenuCollapsableIcon.js';
import '../Icons/MenuDotsIcon.js';
import { MenuIcon } from '../Icons/MenuIcon.js';
import '../Icons/MinusIcon.js';
import '../Icons/NewsIcon.js';
import '../Icons/NtfrIcon.js';
import '../Icons/OkCircleIcon.js';
import '../Icons/OkIcon.js';
import '../Icons/PencilIcon.js';
import '../Icons/PenSquareIcon.js';
import '../Icons/People2Icon.js';
import '../Icons/PeopleIcon.js';
import '../Icons/PersonIcon.js';
import '../Icons/PhoneCallIcon.js';
import '../Icons/PlayFilledIcon.js';
import '../Icons/PlayIcon.js';
import '../Icons/PlusCircleIcon.js';
import '../Icons/PlusIcon.js';
import '../Icons/PlusSquareIcon.js';
import '../Icons/PrintIcon.js';
import '../Icons/RadioIcon.js';
import '../Icons/RefreshCwIcon.js';
import '../Icons/RotateIcon.js';
import '../Icons/SearchIcon.js';
import '../Icons/SettingsIcon.js';
import '../Icons/ShareIcon.js';
import '../Icons/ShieldIcon.js';
import '../Icons/SocialXIcon.js';
import '../Icons/StarFilledIcon.js';
import '../Icons/StarOutlineIcon.js';
import '../Icons/StickyNoteIcon.js';
import '../Icons/SwapHorizontalIcon.js';
import '../Icons/TablePropertiesIcon.js';
import '../Icons/TasklistIcon.js';
import '../Icons/ThumbDownIcon.js';
import '../Icons/ThumbsDownIcon.js';
import '../Icons/ThumbsUpIcon.js';
import '../Icons/ThumbUpIcon.js';
import '../Icons/TimeIcon.js';
import '../Icons/TimerIcon.js';
import '../Icons/Trash2Icon.js';
import '../Icons/TrashIcon.js';
import '../Icons/UpgradeIcon.js';
import '../Icons/UploadIcon.js';
import '../Icons/UserIcon.js';
import '../Icons/VideoIcon.js';
import '../Icons/WorldIcon.js';
import '../Icons/XposiIcon.js';
import '../Icons/YoutubeIcon.js';
import { Logo } from '../Logo/Logo.js';
import { Spinner } from '../Spinner/Spinner.js';
import { SidebarContext } from './SidebarContext.js';
import { SidebarNavigation } from './SidebarNavigation.js';

const StyledIconButton = styled(IconButton).withConfig({
  componentId: "sc-1meyv82-0"
})(["color:", ";"], ({
  theme
}) => theme.components.sidebar.hamburgerColor);
const SidebarHeader = styled.div.withConfig({
  componentId: "sc-1meyv82-1"
})(["display:flex;align-items:center;padding:", ";button{margin-right:", ";}"], ({
  theme
}) => theme.components.sidebar.headerPadding, ({
  theme
}) => theme.components.sidebar.closeButtonMargin);
const SidebarFooter = styled.div.withConfig({
  componentId: "sc-1meyv82-2"
})(["position:relative;bottom:0;left:0;right:0;padding:", ";", ""], ({
  theme
}) => theme.components.sidebar.headerPadding, media.greaterThan('s')`
    position: fixed;
  `);
const SidebarLoader = styled.div.withConfig({
  componentId: "sc-1meyv82-3"
})(["padding:", ";"], ({
  theme
}) => theme.components.sidebar.headerPadding);
const StyledDrawerError = styled(DrawerError).withConfig({
  componentId: "sc-1meyv82-4"
})(["padding:0 ", ";height:calc(100% - 80px);"], spacing(2));
const SidebarContent = /*#__PURE__*/withTitan(({
  isLoading,
  hasError,
  children
}) => {
  if (hasError) {
    return jsx(StyledDrawerError, {
      description: "Ververs de pagina of probeer het later nog eens",
      title: "Helaas we konden geen menu items vinden"
    });
  }
  if (isLoading) {
    return jsx(SidebarLoader, {
      children: jsx(Spinner, {
        size: 48
      })
    });
  }
  return jsx(Fragment, {
    children: children
  });
}, {
  name: 'SidebarContent'
});
const Sidebar = /*#__PURE__*/withTitan(({
  activeId,
  sidebarItems,
  brandName,
  isLoading,
  hasError,
  labelHamburger,
  children,
  testId = 'sidebar',
  matchFunction,
  logoComponent,
  labelClose = 'Sluit menu',
  labelOpen = 'Open menu'
}) => {
  const themeContext = useTheme();
  const [isShown, toggleShown] = useToggle();
  const isLarge = useGreaterThan('s');
  const [selectedDepth, setSelectedDepth] = useState(0);
  const [isSelecting, setIsSelecting] = useState(false);
  const sidebarMenuButtonElementProps = useElementProps('sidebarMenuButton');
  const [state, setState] = useState({
    activeId
  });
  const previousActiveId = usePrevious(activeId);
  const tree = useMemo(() => {
    const data = addHasChildren(sidebarItems);
    return staticTreeSource(data);
  }, [sidebarItems]);
  const [drawerWidth, setDrawerWidth] = useState(themeContext.components.sidebar.primaryNavigationWidthDesktop + selectedDepth * themeContext.components.sidebar.subNavigationWidth);
  const handleHideDrawer = useCallback(() => {
    setIsSelecting(false);
    setState({
      activeId: previousActiveId
    });
    setSelectedDepth(0);
    toggleShown();
  }, [previousActiveId, toggleShown]);
  const handleCloseSubmenu = useCallback(depth => {
    setSelectedDepth(depth);
  }, []);
  const handleClickButton = useCallback(item => {
    setIsSelecting(true);
    setSelectedDepth(item.depth + 1);
  }, [setSelectedDepth]);
  const onClickLink = useCallback(() => {
    setIsSelecting(false);
    toggleShown();
    setSelectedDepth(0);
  }, [toggleShown]);
  useEffect(() => {
    setState({
      activeId
    });
  }, [activeId]);
  useEffect(() => {
    if (isLarge) {
      setDrawerWidth(themeContext.components.sidebar.primaryNavigationWidthDesktop + selectedDepth * themeContext.components.sidebar.subNavigationWidth);
    } else {
      setDrawerWidth(themeContext.components.sidebar.primaryNavigationWidthMobile);
    }
  }, [selectedDepth, isLarge, themeContext.components.sidebar.primaryNavigationWidthDesktop, themeContext.components.sidebar.subNavigationWidth, themeContext.components.sidebar.primaryNavigationWidthMobile]);
  const context = useMemo(() => ({
    closeSidebar: handleHideDrawer
  }), [handleHideDrawer]);
  return jsxs(SidebarContext.Provider, {
    value: context,
    children: [jsx(StyledIconButton, Object.assign({}, sidebarMenuButtonElementProps, {
      hasBackground: false,
      icon: jsx(MenuIcon, {}),
      label: labelOpen,
      padding: 1,
      size: 20,
      testId: `${testId}MenuButtonOpen`,
      variant: "secondary",
      onClick: toggleShown,
      children: labelHamburger
    })), jsxs(Drawer, {
      isShown: isShown,
      testId: `${testId}Drawer`,
      variant: "secondary",
      width: drawerWidth,
      onClose: handleHideDrawer,
      children: [jsxs(SidebarHeader, {
        children: [jsx(IconButton, {
          hasBackground: true,
          icon: jsx(CloseIcon, {}),
          label: labelClose,
          padding: 1,
          size: 24,
          testId: `${testId}MenuButtonClose`,
          variant: "secondary",
          onClick: handleHideDrawer
        }), logoComponent || jsx(Logo, {
          brandName: brandName
        })]
      }), jsx(SidebarContent, {
        hasError: hasError,
        isLoading: isLoading,
        children: jsx(TreeContainer, {
          source: tree,
          state: state,
          onStateChange: setState,
          children: jsx(SidebarNavigation, {
            isSelecting: isSelecting,
            matchFunction: matchFunction,
            selectedDepth: selectedDepth,
            onClickButton: handleClickButton,
            onClickLink: onClickLink,
            onCloseSubMenu: handleCloseSubmenu,
            onHideDrawer: handleHideDrawer
          })
        })
      }), jsx(SidebarFooter, {
        children: children
      })]
    })]
  });
}, {
  name: 'Sidebar'
});

export { Sidebar };
//# sourceMappingURL=Sidebar.js.map
